import Phaser from 'phaser';
import { COLORS, HEX } from '../constants/colors';
import { mapProgress } from '../systems/MapProgress';
import { MAX_LEVEL } from '../constants/progression/LevelConfig';
import { getUiScale } from '../constants/scale';
import { sceneGrid } from '../utils/grid';
import { MusicManager } from '../managers/MusicManager';
import { markMapFirstReady, markMapReadyAfterRotate } from '../platform/perf';

const BOSS_LEVELS = new Set([5, 10, 15, 20, 25, 30, 35, 40, 45]);
// Базовые радиусы точек (на uiScale=1.0). На низком экране × uiScale — на 360 точка
// 7px (с радиусом), что предотвращает налегание при сжатии spacingY.
const POINT_RADIUS_BASE = 14;
const BOSS_POINT_RADIUS_BASE = 22;
// Структурная пересборка W2: 9 регионов по 5 уровней, 45 точек.
// Раскладка ГОРИЗОНТАЛЬНАЯ: регион = столбец (9 столбцов слева направо),
// точка в регионе = ряд (5 рядов сверху вниз). Босс = нижняя точка столбца (inRegion=4).
const POINTS_PER_REGION = 5;
const REGIONS_COUNT = 9;
// Адаптивные отступы сетки точек от краёв сцены.
const POINTS_MARGIN_X = 80;
const POINTS_MARGIN_TOP = 100;
const POINTS_MARGIN_BOTTOM = 80;
const POINTS_MAX_SPACING_X = 110;
const POINTS_MAX_SPACING_Y = 110;
const COLOR_LOCKED = 0x444444;
const COLOR_UNLOCKED = 0xccaa22;
const COLOR_COMPLETED = 0x33aa33;
const COLOR_BOSS_STROKE = 0xff4444;

// Слои фона под сеткой точек. depth -20 = фон, -10 = scrim, 0+ = точки/HUD.
const BG_DEPTH = -20;
const SCRIM_DEPTH = -10;
const SCRIM_ALPHA = 0.7;

export class MapScene extends Phaser.Scene {
  private lastResult?: 'victory' | 'defeat';
  private lastLevel?: number;

  // Подложка карты + scrim для читаемости текста и точек поверх пёстрого фона.
  private bgMap!: Phaser.GameObjects.Image;
  private scrim!: Phaser.GameObjects.Rectangle;

  // Ссылки на статические HUD-тексты — позиционируются через sceneGrid 12×9 в
  // relayoutMap(). Результат опционален (есть только после прохождения уровня).
  // Заголовок «FOREST GUARDIAN — Карта» убран: CTA-кнопка теперь занимает
  // верхнюю строку, ниже точек становится свободнее (босс-точки 5/10/.../45
  // не перекрываются на низком вьюпорте).
  private resultText?: Phaser.GameObjects.Text;
  private moneyText!: Phaser.GameObjects.Text;

  // Шорткат-кнопка «В БОЙ» — основной CTA на мобильном (точки в сетке 9×5 на
  // 360px ширины тапаются плохо). Прыгает на getNextPlayableLevel().
  private enterBtn!: Phaser.GameObjects.Rectangle;
  private enterBtnLabel!: Phaser.GameObjects.Text;
  // Целевой уровень для тапа по enterBtn. null = карта пройдена, тап игнорится.
  // Хранится в поле, чтобы pointerdown подписать ОДИН раз через стабильный
  // handler и не пересоздавать listener при ререндере состояния кнопки.
  private pendingLevel: number | null = null;

  // Ссылки на точки уровней — shape (Arc для обычных, Star для боссов) +
  // текст-номер. На resize двигаем по getPointPosition() вместо пересоздания
  // (быстрее, нет destroyed/recreated). Общий тип Shape, оба наследуются.
  private levelDots: Map<
    number,
    { circle: Phaser.GameObjects.Shape; label: Phaser.GameObjects.Text }
  > = new Map();

  constructor() {
    super({ key: 'MapScene' });
  }

  init(data: { lastResult?: 'victory' | 'defeat'; level?: number }): void {
    this.lastResult = data?.lastResult;
    this.lastLevel = data?.level;
    this.levelDots = new Map();
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLORS.black);

    // Подложка: карта-террейн (как на MenuScene), cover-fit в relayoutMap().
    this.bgMap = this.add.image(0, 0, 'map_bg').setOrigin(0.5).setDepth(BG_DEPTH);
    // Scrim — затемнение под HUD/сеткой. На карте сильнее (0.55), чем на меню,
    // потому что серые «locked» точки на пёстром фоне теряются.
    this.scrim = this.add
      .rectangle(0, 0, 1, 1, COLORS.black, SCRIM_ALPHA)
      .setOrigin(0.5)
      .setDepth(SCRIM_DEPTH);

    if (this.lastResult === 'victory' && this.lastLevel) {
      this.resultText = this.add
        .text(0, 0, `Точка ${this.lastLevel} пройдена`, {
          fontFamily: 'Arial',
          fontSize: '16px',
          color: HEX.green,
        })
        .setOrigin(0.5);
    } else if (this.lastResult === 'defeat' && this.lastLevel) {
      this.resultText = this.add
        .text(0, 0, `Точка ${this.lastLevel} провалена — попробуй снова`, {
          fontFamily: 'Arial',
          fontSize: '16px',
          color: HEX.red,
        })
        .setOrigin(0.5);
    }

    this.moneyText = this.add
      .text(0, 0, `Деньги: ${mapProgress.getData().money}`, {
        fontFamily: 'Arial',
        fontSize: '18px',
        color: HEX.yellow,
      })
      .setOrigin(1, 0);

    this.drawMap();

    // CTA «В БОЙ» — крупная кнопка снизу. На мобильном жмётся надёжно, точки
    // в сетке оставлены для retry уже пройденных уровней. Размер не 0×0 на
    // старте: Phaser кеширует hitArea при setInteractive, и если кнопку создать
    // нулевой — последующий setSize hitArea не обновит. relayoutMap() ниже
    // перепишет под uiScale, но дефолтная стартовая геометрия должна быть валидной.
    this.enterBtn = this.add
      .rectangle(0, 0, 240, 56, COLOR_UNLOCKED)
      .setStrokeStyle(2, 0xffffff)
      .setInteractive({ useHandCursor: true });
    this.enterBtn.on('pointerdown', () => {
      if (this.pendingLevel != null) this.enterLevel(this.pendingLevel);
    });
    this.enterBtnLabel = this.add
      .text(0, 0, '', {
        fontFamily: 'Arial',
        fontSize: '22px',
        color: HEX.black,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.updateEnterButton();

    // Музыка меню. Если AudioContext ещё locked (до первого жеста), MusicManager
    // сам подпишется на phaser-событие 'unlocked' и стартует трек при первом
    // тапе. Если уже unlocked (вернулись после боя) — играет сразу. На рестарт
    // волны / повторный заход в MapScene с тем же треком — no-op.
    MusicManager.getInstance().playMenu(this);

    // Раскладка через grid + реактивность на resize. Раньше handleResize делал
    // scene.restart() с дебаунсом 250мс — теперь просто перевыставляем позиции.
    // RAF-coalescing: после orientationchange iOS Safari эмитит несколько
    // resize за один-два фрейма (matchMedia → screen.orientation → window),
    // без коалесcинга relayoutMap (45 точек + cover-fit фона) проигрывался
    // каждый раз, давая видимую задержку. requestAnimationFrame склеивает
    // их в один пересчёт перед ближайшим рендером.
    this.relayoutMap();
    this.scale.on('resize', this.scheduleRelayout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.scheduleRelayout, this);
    });
  }

  private relayoutPending = false;

  private scheduleRelayout(): void {
    if (this.relayoutPending) return;
    this.relayoutPending = true;
    requestAnimationFrame(() => {
      this.relayoutPending = false;
      this.relayoutMap();
    });
  }

  private relayoutMap(): void {
    const sceneW = this.scale.width;
    const sceneH = this.scale.height;

    // Подложка: cover-fit (заполнить canvas с обрезкой лишнего, без искажения).
    const tex = this.bgMap.texture.getSourceImage() as HTMLImageElement | HTMLCanvasElement;
    const coverScale = Math.max(sceneW / tex.width, sceneH / tex.height);
    this.bgMap.setScale(coverScale);
    this.bgMap.setPosition(sceneW / 2, sceneH / 2);
    this.scrim.setPosition(sceneW / 2, sceneH / 2);
    this.scrim.setSize(sceneW, sceneH);

    // sceneGrid 12×9 — те же координаты, что у HUD GameScene. Padding'и × uiScale
    // (как в GameScene) — на маленьких экранах тексты не съедают пол-карты.
    const g = sceneGrid(this);
    const ui = getUiScale(this);
    const pad = 24 * ui;

    const top = g.cell(0, 0);
    const topRight = g.cell(g.cols - 1, 0);
    const topCenter = g.cell(Math.floor(g.cols / 2 - 2), 0, { c: 4 });

    this.moneyText.setPosition(topRight.right - pad, top.y + 30 * ui);

    // Размер кнопки — clamp снизу: на 360px ширине uiScale ~0.6 даёт 168×36,
    // что мельче mobile-friendly 44px. Жёсткий floor 200×48 гарантирует тап.
    const btnW = Math.max(200, 280 * ui);
    const btnH = Math.max(48, 60 * ui);
    this.enterBtn.setSize(btnW, btnH);
    // setSize у Rectangle обновляет VISUAL bounds, но НЕ обновляет input.hitArea,
    // которая закешилась на стартовых 240×56 при setInteractive в create().
    // Пересинхронизируем hit-area вручную, иначе тап работает только по дефолту.
    // hitArea для setInteractive() по дефолту = (0, 0, w, h) В ЛОКАЛЬНЫХ
    // координатах: Phaser сам смещает point через displayOrigin (w/2, h/2 у
    // origin 0.5). Если поставить (-w/2, -h/2, w, h) — активной будет только
    // верхне-левая четверть кнопки (по тому смещению).
    if (this.enterBtn.input?.hitArea instanceof Phaser.Geom.Rectangle) {
      this.enterBtn.input.hitArea.setTo(0, 0, btnW, btnH);
    }
    // CTA наверх, на место бывшего titleText. Центр Y = btnH/2 + 12*ui от верха
    // сцены — сохраняем небольшой padding от края canvas.
    const btnCY = top.y + btnH / 2 + 12 * ui;
    this.enterBtn.setPosition(topCenter.cx, btnCY);
    this.enterBtnLabel.setPosition(topCenter.cx, btnCY);

    // resultText (Точка N пройдена/провалена) уезжает ПОД кнопку, чтобы не
    // перекрывалась. Только если есть данные о прошлом матче.
    if (this.resultText) {
      this.resultText.setPosition(topCenter.cx, btnCY + btnH / 2 + 16 * ui);
    }

    // Точки уровней — внутренняя 9×5 геометрия в getPointPosition() пересчитывается
    // на текущий scale при каждом вызове, надо только перепозиционировать circle+label.
    for (let level = 1; level <= MAX_LEVEL; level++) {
      const dot = this.levelDots.get(level);
      if (!dot) continue;
      const { x, y } = this.getPointPosition(level);
      dot.circle.setPosition(x, y);
      dot.label.setPosition(x, y);
    }

    // Замеры карты после того как всё разложено:
    // 1. Первый заход → фиксируем «нavigation → карта видна» (page.map).
    // 2. Каждый заход в rotate-окне → обновляем lastRotateMs (перезапишется
    //    на всех последующих relayout'ах шторма — финальное значение честно
    //    показывает, когда карта окончательно застыла).
    markMapFirstReady();
    markMapReadyAfterRotate();
  }

  private drawMap(): void {
    for (let level = 1; level <= MAX_LEVEL; level++) {
      const isBoss = BOSS_LEVELS.has(level);
      const unlocked = mapProgress.isLevelUnlocked(level);
      const completed = mapProgress.isLevelCompleted(level);

      let color = COLOR_LOCKED;
      if (completed) color = COLOR_COMPLETED;
      else if (unlocked) color = COLOR_UNLOCKED;

      const ui = getUiScale(this);
      const radius = (isBoss ? BOSS_POINT_RADIUS_BASE : POINT_RADIUS_BASE) * ui;
      // Позиция (0, 0) — relayoutMap() сдвинет на правильное место.
      // Boss-точки — 5-конечная звезда (outer = radius, inner = radius × 0.45 —
      // компромисс: лучи различимы, цифра уровня помещается в центр). Обычные
      // — круг как раньше.
      const point: Phaser.GameObjects.Shape = isBoss
        ? this.add.star(0, 0, 5, radius * 0.45, radius, color)
        : this.add.circle(0, 0, radius, color);
      if (isBoss) point.setStrokeStyle(Math.max(1, 3 * ui), COLOR_BOSS_STROKE);

      const labelFontSize = `${Math.max(9, Math.round((isBoss ? 14 : 12) * ui))}px`;
      const label = this.add
        .text(0, 0, String(level), {
          fontFamily: 'Arial',
          fontSize: labelFontSize,
          color: HEX.white,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      if (unlocked) {
        point.setInteractive({ useHandCursor: true });
        point.on('pointerdown', () => this.enterLevel(level));
      }

      this.levelDots.set(level, { circle: point, label });
    }
    // Текстовые заголовки регионов («Регион N») убраны — на W13 регионы разделит арт-подложка.
  }

  private getPointPosition(level: number): { x: number; y: number } {
    // region = столбец (0..8), inRegion = ряд внутри столбца (0..4, 4 = босс снизу).
    // Прогрессия: ↓ внутри столбца, затем → в следующий столбец.
    const region = Math.floor((level - 1) / POINTS_PER_REGION);
    const inRegion = (level - 1) % POINTS_PER_REGION;
    // Spacing = clamp(min, доступная_ось / N, max). Min — диаметр boss-точки × ui + 5px
    // запас (точки в столбце не налезают на TG/низком экране). Max — POINTS_MAX_SPACING
    // (на широком экране сетка не размазывается, центрируется группой).
    const ui = getUiScale(this);
    const minSpacing = BOSS_POINT_RADIUS_BASE * ui * 2 + 5;
    const availableWidth = this.scale.width - 2 * POINTS_MARGIN_X;
    const availableHeight = this.scale.height - POINTS_MARGIN_TOP - POINTS_MARGIN_BOTTOM;
    // X — по столбцам (9 шт), Y — по рядам (5 шт).
    const spacingX = Phaser.Math.Clamp(
      availableWidth / (REGIONS_COUNT - 1),
      minSpacing,
      POINTS_MAX_SPACING_X,
    );
    const spacingY = Phaser.Math.Clamp(
      availableHeight / (POINTS_PER_REGION - 1),
      minSpacing,
      POINTS_MAX_SPACING_Y,
    );
    const gridWidth = spacingX * (REGIONS_COUNT - 1);
    const gridHeight = spacingY * (POINTS_PER_REGION - 1);
    const startX = (this.scale.width - gridWidth) / 2;
    const startY = (this.scale.height - gridHeight) / 2;
    const x = startX + region * spacingX;
    const y = startY + inRegion * spacingY;
    return { x, y };
  }

  private updateEnterButton(): void {
    const next = mapProgress.getNextPlayableLevel();
    this.pendingLevel = next;
    if (next == null) {
      this.enterBtnLabel.setText('ВСЁ ПРОЙДЕНО');
      this.enterBtn.setFillStyle(COLOR_LOCKED);
      this.enterBtn.setAlpha(0.6);
      return;
    }
    // Лаконично «В БОЙ» без номера/(БОСС) — точку всё равно видно на карте
    // (она пульсирует/выделена как next), а кнопка должна читаться мгновенно.
    this.enterBtnLabel.setText('В БОЙ');
    this.enterBtn.setFillStyle(COLOR_UNLOCKED);
    this.enterBtn.setAlpha(1);
  }

  private enterLevel(level: number): void {
    this.scene.start('GameScene', { level });
  }
}
